/**
 * Mobile WebView Bridge
 *
 * Handles bidirectional communication between React Native WebView and the web app.
 *
 * INBOUND (Mobile → Web):
 *   URL params when opening WebView: organizationUnid, accessToken, refreshToken, expiresIn, idToken, platform
 *   These are parsed on init and used to authenticate the web session.
 *
 * OUTBOUND (Web → Mobile):
 *   Callbacks registered by mobile via window.registerMobileCallback():
 *     - onBack: navigate back in mobile app
 *     - onLogin: user logged in (sends auth data)
 *     - onLogout: user logged out
 *     - onOrganizationChanged: organization info updated (sends org unid)
 *     - onCurrentOrganizationChanged: current organization switched (sends org unid)
 *     - onTokenRefreshed: token was refreshed (sends new auth data)
 */

import { useAuthStore } from './stores/auth-store';
import { useOrganizationStore } from './stores/organization-store';
import { TOKEN_KEY, REFRESH_TOKEN_KEY, TOKEN_EXPIRES_AT_KEY } from './api/axiosIntance';
import { setCookie } from './utils/cookies';
import type { Organization } from '../types';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface MobileBridgeParams {
  organizationUnid: string;
  accessToken: string;
  refreshToken: string;
  expiresIn: string; // seconds as string from URL
  idToken: string;
  platform: 'ios' | 'android' | string;
}

interface RegisteredCallbacks {
  [key: string]: (...args: any[]) => any;
}

// ─── Callback Registry ──────────────────────────────────────────────────────

const registeredCallbacks: RegisteredCallbacks = {};

/** Register a callback from mobile */
const registerMobileCallback = (
  name: string,
  callback: (...args: any[]) => any,
): { success: boolean; error?: string } => {
  try {
    if (!name || typeof name !== 'string') {
      return { success: false, error: 'Callback name must be a non-empty string' };
    }
    if (typeof callback !== 'function') {
      return { success: false, error: 'Callback must be a function' };
    }
    registeredCallbacks[name] = callback;
    console.log(`[MobileBridge] ✅ Callback registered: ${name}`);
    return { success: true };
  } catch (error) {
    console.error('[MobileBridge] Error registering callback:', error);
    return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
  }
};

/** Unregister a callback */
const unregisterMobileCallback = (name: string): { success: boolean; error?: string } => {
  if (registeredCallbacks[name]) {
    delete registeredCallbacks[name];
    console.log(`[MobileBridge] ✅ Callback unregistered: ${name}`);
    return { success: true };
  }
  return { success: false, error: `Callback ${name} not found` };
};

/** Check if a callback is registered */
const isCallbackRegistered = (name: string): boolean => name in registeredCallbacks;

/** Safely call a registered callback */
const callMobileCallback = (name: string, ...args: any[]): any => {
  const callback = registeredCallbacks[name];
  if (!callback) {
    console.warn(`[MobileBridge] Callback ${name} is not registered, skipping`);
    return undefined;
  }
  try {
    return callback(...args);
  } catch (error) {
    console.error(`[MobileBridge] Error calling callback ${name}:`, error);
    return undefined;
  }
};

// ─── Detect WebView ─────────────────────────────────────────────────────────

const MOBILE_PLATFORM_KEY = 'mobile_platform';

/** Whether the app is currently running inside a mobile WebView */
export const isRunningInWebView = (): boolean => {
  return !!(window as any).__MOBILE_WEBVIEW__ || getPlatformFromUrl() !== null || !!localStorage.getItem(MOBILE_PLATFORM_KEY);
};

/** Get the saved mobile platform ('ios' | 'android' | null) */
export const getMobilePlatform = (): string | null => {
  return (window as any).__MOBILE_PLATFORM__ || localStorage.getItem(MOBILE_PLATFORM_KEY);
};

/** Get platform from URL params */
const getPlatformFromUrl = (): string | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('platform');
  } catch {
    return null;
  }
};

// ─── Inbound: Parse URL params & set auth ───────────────────────────────────

/** Parse mobile params from URL query string */
const parseMobileParams = (): MobileBridgeParams | null => {
  try {
    const params = new URLSearchParams(window.location.search);
    const accessToken = params.get('accessToken');

    // At minimum we need accessToken
    if (!accessToken) {
      return null;
    }

    return {
      organizationUnid: params.get('organizationUnid') || '',
      accessToken,
      refreshToken: params.get('refreshToken') || '',
      expiresIn: params.get('expiresIn') || '',
      idToken: params.get('idToken') || '',
      platform: params.get('platform') || '',
    };
  } catch {
    return null;
  }
};

/** Apply auth data from mobile params into stores & localStorage */
const applyMobileAuth = (params: MobileBridgeParams): void => {
  const { accessToken, refreshToken, expiresIn, organizationUnid } = params;

  // 1. Save tokens to localStorage (for axios interceptor to read)
  localStorage.setItem(TOKEN_KEY, accessToken);
  setCookie(TOKEN_KEY, accessToken, 7);

  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    setCookie(REFRESH_TOKEN_KEY, refreshToken, 7);
  }

  if (expiresIn) {
    const expiresInSeconds = parseInt(expiresIn, 10);
    if (!isNaN(expiresInSeconds)) {
      const expiresAtMs = Date.now() + expiresInSeconds * 1000;
      localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAtMs.toString());
    }
  }

  // 2. Set auth store via proper action
  const expiresAtIso = expiresIn
    ? new Date(Date.now() + parseInt(expiresIn, 10) * 1000).toISOString()
    : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

  useAuthStore.getState().setAuthResponse({
    accessToken,
    refreshToken: refreshToken || '',
    expiresAt: expiresAtIso,
  });

  // 3. Set organization via proper action if provided
  if (organizationUnid) {
    const orgStore = useOrganizationStore.getState();
    const { organizations } = orgStore;

    // Try to find the org in the already-loaded list
    const matchedOrg = organizations.find(
      (org) => org.id === organizationUnid,
    );

    if (matchedOrg) {
      orgStore.setOrganization(matchedOrg);
    } else {
      // Set a placeholder org — MaintenanceLayout will resolve full org from API
      orgStore.setOrganization({
        id: organizationUnid,
        name: '',
        roles: ['admin'],
        status: 'active',
      } as any);
    }
  }

  console.log('[MobileBridge] ✅ Auth applied from mobile params');
};

// ─── Outbound: Web → Mobile callbacks ───────────────────────────────────────

/** Notify mobile: user wants to go back */
export const onBack = (fallback?: () => void): void => {
  if (isCallbackRegistered('onBack')) {
    callMobileCallback('onBack', {});
    console.log('[MobileBridge] ✅ onBack callback called');
  } else {
    fallback?.();
  }
};

/** Notify mobile: user logged in */
export const onLogin = (data: {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
  idToken?: string;
}): void => {
  if (isCallbackRegistered('onLogin')) {
    callMobileCallback('onLogin', data);
    console.log('[MobileBridge] ✅ onLogin callback called');
  }
};

/** Notify mobile: user logged out */
export const onLogout = (): void => {
  if (isCallbackRegistered('onLogout')) {
    callMobileCallback('onLogout', {});
    console.log('[MobileBridge] ✅ onLogout callback called');
  }
};

/** Notify mobile: organization info was updated */
export const onOrganizationChanged = (organizationUnid: string | null): void => {
  if (isCallbackRegistered('onOrganizationChanged')) {
    callMobileCallback('onOrganizationChanged', {
      organizationUnid,
    });
    console.log('[MobileBridge] ✅ onOrganizationChanged callback called');
  }
};

/** Notify mobile: current organization was switched */
export const onCurrentOrganizationChanged = (organizationUnid: string | null): void => {
  if (isCallbackRegistered('onCurrentOrganizationChanged')) {
    callMobileCallback('onCurrentOrganizationChanged', {
      organizationUnid,
    });
    console.log('[MobileBridge] ✅ onCurrentOrganizationChanged callback called');
  }
};

/** Notify mobile: token was refreshed */
export const onTokenRefreshed = (data: {
  accessToken: string;
  refreshToken: string;
  expiresIn?: number;
}): void => {
  if (isCallbackRegistered('onTokenRefreshed')) {
    callMobileCallback('onTokenRefreshed', data);
    console.log('[MobileBridge] ✅ onTokenRefreshed callback called');
  }
};

// ─── Window API for mobile to read state ────────────────────────────────────

/** Get the current auth tokens from localStorage */
const getLatestAuth = () => {
  const accessToken = localStorage.getItem(TOKEN_KEY);
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  const expiresAt = localStorage.getItem(TOKEN_EXPIRES_AT_KEY);

  if (!accessToken) return null;

  return { accessToken, refreshToken, expiresAt };
};

/** Set auth tokens from mobile into stores & localStorage */
const setLatestAuth = (auth: {
  accessToken: string;
  refreshToken?: string;
  expiresIn?: number;
} | null): { success: boolean; error?: string } => {
  try {
    if (!auth) {
      // Clear auth
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(TOKEN_EXPIRES_AT_KEY);
      useAuthStore.getState().logout();
      console.log('[MobileBridge] ✅ setLatestAuth: cleared');
      return { success: true };
    }

    const { accessToken, refreshToken, expiresIn } = auth;

    if (!accessToken || typeof accessToken !== 'string') {
      return { success: false, error: 'accessToken must be a non-empty string' };
    }

    // Save to localStorage
    localStorage.setItem(TOKEN_KEY, accessToken);
    setCookie(TOKEN_KEY, accessToken, 7);

    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
      setCookie(REFRESH_TOKEN_KEY, refreshToken, 7);
    }

    if (expiresIn && typeof expiresIn === 'number') {
      const expiresAtMs = Date.now() + expiresIn * 1000;
      localStorage.setItem(TOKEN_EXPIRES_AT_KEY, expiresAtMs.toString());
    }

    // Update auth store
    const expiresAtIso = expiresIn
      ? new Date(Date.now() + expiresIn * 1000).toISOString()
      : new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString();

    useAuthStore.getState().setAuthResponse({
      accessToken,
      refreshToken: refreshToken || '',
      expiresAt: expiresAtIso,
    });

    console.log('[MobileBridge] ✅ setLatestAuth: applied');
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

/** Get current organization unid */
const getCurrentOrganizationUnid = (): string | null => {
  const org = useOrganizationStore.getState().organization;
  return org?.id || null;
};

/** Set current organization by unid */
const setCurrentOrganizationUnid = (
  organizationUnid: string,
): { success: boolean; error?: string } => {
  try {
    if (!organizationUnid || typeof organizationUnid !== 'string') {
      return { success: false, error: 'Organization unid must be a non-empty string' };
    }

    const { organizations, setOrganization } = useOrganizationStore.getState();
    const organization = organizations.find(
      (org) => org.id === organizationUnid || (org as any).unid === organizationUnid,
    );

    if (!organization) {
      return {
        success: false,
        error: `Organization with unid ${organizationUnid} not found`,
      };
    }

    setOrganization(organization);
    onCurrentOrganizationChanged(organization?.id || null);
    console.log('[MobileBridge] ✅ setCurrentOrganizationUnid:', organizationUnid);
    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error',
    };
  }
};

// ─── Clean URL after parsing mobile params ──────────────────────────────────

const cleanMobileParamsFromUrl = (): void => {
  try {
    const url = new URL(window.location.href);
    const mobileParamKeys = [
      'organizationUnid',
      'accessToken',
      'refreshToken',
      'expiresIn',
      'idToken',
      'platform',
    ];
    let hadParams = false;
    mobileParamKeys.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        hadParams = true;
      }
    });
    if (hadParams) {
      window.history.replaceState({}, '', url.pathname + url.search);
    }
  } catch {
    // silently ignore
  }
};

// ─── Initialize ─────────────────────────────────────────────────────────────

export const initMobileBridge = (): void => {
  // 1. Parse inbound params and apply auth
  const params = parseMobileParams();
  if (params) {
    (window as any).__MOBILE_WEBVIEW__ = true;
    (window as any).__MOBILE_PLATFORM__ = params.platform;
    if (params.platform) {
      localStorage.setItem(MOBILE_PLATFORM_KEY, params.platform);
    }
    applyMobileAuth(params);
    cleanMobileParamsFromUrl();
    console.log(`[MobileBridge] Initialized from WebView (platform: ${params.platform})`);
  }

  // 2. Expose registration API to window for mobile to call
  (window as any).registerMobileCallback = registerMobileCallback;
  (window as any).unregisterMobileCallback = unregisterMobileCallback;
  (window as any).isCallbackRegistered = isCallbackRegistered;

  // 3. Expose state-reading API to window
  (window as any).getLatestAuth = getLatestAuth;
  (window as any).setLatestAuth = setLatestAuth;
  (window as any).getCurrentOrganizationUnid = getCurrentOrganizationUnid;
  (window as any).setCurrentOrganizationUnid = setCurrentOrganizationUnid;

  console.log('[MobileBridge] Bridge initialized. Available window APIs:');
  console.log('  - window.registerMobileCallback(name, callback)');
  console.log('  - window.unregisterMobileCallback(name)');
  console.log('  - window.isCallbackRegistered(name)');
  console.log('  - window.getLatestAuth()');
  console.log('  - window.setLatestAuth(auth)');
  console.log('  - window.getCurrentOrganizationUnid()');
  console.log('  - window.setCurrentOrganizationUnid(unid)');
  console.log('  Outbound callbacks: onBack, onLogin, onLogout, onOrganizationChanged, onCurrentOrganizationChanged, onTokenRefreshed');
};
